import { getEnvironmentSettings, validateIdp } from '@skyslope/auth-config';
import { createUserManager, type UserManager, TokenDomain, type LoginOpts } from '@skyslope/auth-js';

import {
  azureFNFIDP,
  senderUIClientId,
  templateUIClientId,
  oktaEnv,
  primeAuthClientId,
  standaloneLoginUrl,
} from '../lib/environment-config';
import { captureFrameOrigin, withFrameOrigin } from '../utils/frame-origin';
import { captureSessionlessHint, withSessionlessHint } from '../utils/sessionless-hint';
import { dropImportedToken, isWidgetTokenSession, listenForWidgetTokens, requestTokenFromWidget } from './widget-token';

// The user managers below are built when this module loads, before index.tsx runs, and auth-js
// rewrites the entry URL as it starts. So read frameOrigin and the sessionless hint first.
captureFrameOrigin();
captureSessionlessHint();

// True when Forms framed us in its widget and runs on a widget token (AP-3360): the session comes
// from a token the widget hands over, not an Okta cookie. Every other case is unchanged.
const widgetTokenSession = isWidgetTokenSession();

// auth-js exchanges the widget's token for a Global token, and importToken only keeps a token of the
// manager's own domain. Our APIs accept the Global issuer.
const senderTokenDomain = widgetTokenSession ? TokenDomain.Global : TokenDomain.DigiSign;

// Both managers get the same features because auth-js keeps one shared copy of them.
// - disablePageRefresh: false keeps auth-js 1.x's reload when the Okta or impersonated user changes,
//   in widget mode too. A reload there asks the widget for a token again.
// - allowSessionless: auth-js 2.4.0 needs it for importToken and its token-session handling. 2.3.0
//   ignores it, and a variable (not an inline object) keeps it type-checking against 2.3.0's types.
const features = {
  disablePageRefresh: false,
  allowSessionless: true,
};

// auth-js clears sessionStorage before an Okta sign-out leaves the page, so in a widget token session
// /logged_out keeps widget mode only through these URL params.
const senderPostLogoutPath = widgetTokenSession ? withSessionlessHint(withFrameOrigin('/logged_out')) : '/logged_out';

const scopes = {
  prime: [
    'openid',
    'email',
    'profile',
    'com.skyslope.prime.accountid',
    'com.skyslope.digisign.userid',
    'com.skyslope.prime.subscriber',
    'com.skyslope.groups',
  ],
  forms: [
    'openid',
    'email',
    'profile',
    'com.skyslope.forms.accountid',
    'com.skyslope.digisign.userid',
    'com.skyslope.prime.subscriber',
    'com.skyslope.groups',
  ],
  fnf: [
    'openid',
    'email',
    'profile',
    'com.skyslope.digisign.userid',
    'com.skyslope.fnf.accountid',
    'com.skyslope.groups',
  ],
};

function getIdpIdByKey(idpKey: string): string | null {
  const idpOktaIds = {
    fnf: azureFNFIDP,
    ...userManager.idps,
  };

  // Attempt to dereference Okta IDP ID by friendly name
  const idpByName =
    idpOktaIds[idpKey as keyof typeof idpOktaIds] ||
    idpOktaIds.associations[idpKey as keyof typeof idpOktaIds.associations] ||
    idpOktaIds.platforms[idpKey as keyof typeof idpOktaIds.platforms] ||
    idpOktaIds.brokers[idpKey as keyof typeof idpOktaIds.brokers];

  return typeof idpByName === 'string' ? idpByName : validateIdp(idpKey) ? idpKey : null;
}

function getIdp(): string | null {
  const idpStorageKey = 'idp';
  const params = new URLSearchParams(window.location.search);
  const idp = params.get(idpStorageKey) ?? localStorage.getItem(idpStorageKey);
  if (idp) localStorage.setItem(idpStorageKey, idp);
  return idp;
}

function getIdpOktaId(): string | null {
  const idpKey = getIdp();
  if (!idpKey || idpKey === 'none') return null;
  return getIdpIdByKey(idpKey);
}

function getScopes(): string[] {
  if (document.referrer.includes('forms.skyslope') || document.referrer.includes('offers.skyslope')) {
    return scopes.forms;
  }
  const idp = getIdp();
  return idp && idp in scopes ? scopes[idp as keyof typeof scopes] : scopes.prime;
}

async function onAuthRequired(um: UserManager) {
  // In a widget token session an Okta redirect would dead-end on Okta's cookie page, so ask the
  // widget for a token instead. The templates manager keeps the normal login.
  if (widgetTokenSession && um === userManager) {
    // Wait for the boot-time clear, so it cannot wipe a token the widget sends.
    await authReady;
    await requestTokenFromWidget(um);
    return;
  }

  const idp = getIdpOktaId();
  const loginOpts: LoginOpts = {
    provider: {
      idp: idp || undefined,
      uri: !idp && standaloneLoginUrl ? standaloneLoginUrl : undefined,
    },
  };

  return um.login(loginOpts);
}

const userManager = createUserManager({
  environment: oktaEnv,
  tokenDomain: senderTokenDomain,
  providers: {
    okta: {
      clientId: senderUIClientId,
      redirectPath: '/auth/callback',
      postLogoutRedirectPath: senderPostLogoutPath,
      scopes: getScopes(),
    },
    primeAuth: {
      clientId: primeAuthClientId,
      redirectPath: '/auth/callback/prime',
      postLogoutRedirectPath: '/logged_out',
    },
  },
  onAuthRequired,
  features,
});

const userManagerTemplates = createUserManager({
  environment: oktaEnv,
  tokenDomain: TokenDomain.DigiSign,
  providers: {
    okta: {
      clientId: templateUIClientId,
      redirectPath: '/templates/auth/callback',
      postLogoutRedirectPath: '/templates/logged_out',
      scopes: [
        'openid',
        'email',
        'profile',
        'com.skyslope.digisign.userrole',
        'com.skyslope.digisign.accountid',
        'com.skyslope.digisign.userid',
        'com.skyslope.groups',
      ],
    },
  },
  onAuthRequired,
  features,
});

const SIGN_OUT_PATHS = ['/logout', '/logged_out'];

// Each page load drops any token this frame imported and asks the widget again: the host may have
// switched or signed out its user while Forms held the frame.
async function startWidgetTokenSession(): Promise<void> {
  await dropImportedToken(userManager);
  // The sign-out pages take no tokens at all: /logout only signs out (a token imported now would
  // make it wait), and /logged_out, where an Okta sign-out from /logout lands, must stay signed out.
  if (SIGN_OUT_PATHS.includes(window.location.pathname)) return;
  // Listen only after the clear, so the clear cannot wipe a token the widget sends meanwhile.
  listenForWidgetTokens(userManager);
  if (usesWidgetSignIn()) void requestTokenFromWidget(userManager);
}

/** Resolves when the app may render: in a widget token session, once any stored token is gone. */
export const authReady: Promise<void> = widgetTokenSession ? startWidgetTokenSession() : Promise.resolve();

/**
 * False only when Okta says this browser has no session (401/403 from /users/me). A widget sign-out
 * must also end a normal Okta session beside it, so errors count as "may have one". In a
 * cookie-blocked frame Okta never sees its cookie and answers 403.
 */
export async function mayHaveOktaSession(): Promise<boolean> {
  try {
    const response = await fetch(`${getEnvironmentSettings(oktaEnv).okta.baseUrl}/api/v1/users/me`, {
      credentials: 'include',
    });
    return response.status !== 401 && response.status !== 403;
  } catch {
    return true;
  }
}

export function getUserManager() {
  return /\/templates/.test(window.location.toString()) ? userManagerTemplates : userManager;
}

/**
 * True when this page signs in through the Forms widget: a widget token session on the sender's own
 * pages. The templates pages keep the normal Okta login even inside the widget.
 */
export function usesWidgetSignIn(): boolean {
  return widgetTokenSession && getUserManager() === userManager;
}

export async function getAccessToken() {
  const um = getUserManager();
  return um.getAccessToken();
}

export async function getGlobalToken() {
  const um = getUserManager();
  // In a widget token session the session token is already a Global token (senderTokenDomain).
  return um.getAccessToken(TokenDomain.Global);
}
